import { computed, inject, Injectable, signal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  StoreModelNameByVaultCode,
  StoreModelNames,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { DaliService } from "@bitwarden/web-vault/app/services/dali/dali.service";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { VaultReader } from "@bitwarden/web-vault/app/services/dali/vault.reader";

export interface VaultItemGroup {
  contentHash: string;
  representative: StoreData;
  all: StoreData[];
  count: number;
}

@Injectable({
  providedIn: "root",
})
export class VaultExplorerService {
  private log = inject(LogService);
  private daliService = inject(DaliService);
  private vaultReader = new VaultReader();

  private _vaultData = signal<Map<VaultCodeEnum, StoreData[]> | null>(null);
  readonly vaultData = this._vaultData.asReadonly();

  private _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private _loadingType = signal(false);
  readonly loadingType = this._loadingType.asReadonly();

  private _vaultTypeCounts = signal<Map<VaultCodeEnum, number> | null>(null);
  readonly vaultTypeCounts = this._vaultTypeCounts.asReadonly();

  readonly availableTypes = computed<VaultCodeEnum[]>(() => {
    const counts = this._vaultTypeCounts();
    if (counts) {
      return Array.from(counts.keys())
        .filter((code) => code !== VaultCodeEnum.UNKNOWN)
        .sort((a, b) => this.typeLabel(a).localeCompare(this.typeLabel(b)));
    }
    const data = this._vaultData();
    if (!data) {
      return [];
    }
    return Array.from(data.keys())
      .filter((code) => code !== VaultCodeEnum.UNKNOWN)
      .sort((a, b) => this.typeLabel(a).localeCompare(this.typeLabel(b)));
  });

  /**
   * Loads all vault data once, caches it, and exposes type names with counts.
   * Selecting a type afterwards reads from the cache instantly.
   */
  async loadVaultTypes(): Promise<void> {
    if (this._loading()) {
      return;
    }
    this._loading.set(true);
    try {
      const typeCounts = await this.vaultReader.loadAndCacheVault();
      this._vaultTypeCounts.set(typeCounts);
    } catch (e) {
      this.log.error("VaultExplorerService: Failed to load vault types");
      this.log.error(e);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Serves cached data for the selected type (no vault fetch).
   */
  loadVaultDataForType(type: VaultCodeEnum): void {
    const items = this.vaultReader.getDataForType(type);
    const current = this._vaultData() ?? new Map<VaultCodeEnum, StoreData[]>();
    const updated = new Map(current);
    updated.set(type, items as StoreData[]);
    this._vaultData.set(updated);
  }

  /**
   * Reloads a single type from the vault (e.g. after a delete) and updates the cache.
   */
  async reloadVaultDataForType(type: VaultCodeEnum): Promise<void> {
    if (this._loadingType()) {
      return;
    }
    this._loadingType.set(true);
    try {
      const items = await this.vaultReader.reloadDataForType(type);
      const current = this._vaultData() ?? new Map<VaultCodeEnum, StoreData[]>();
      const updated = new Map(current);
      updated.set(type, items as StoreData[]);
      this._vaultData.set(updated);
    } catch (e) {
      this.log.error(`VaultExplorerService: Failed to reload vault data for type ${type}`);
      this.log.error(e);
    } finally {
      this._loadingType.set(false);
    }
  }

  async loadVaultData(): Promise<void> {
    if (this._loading()) {
      return;
    }
    this._loading.set(true);
    try {
      const data = await this.vaultReader.loadVault();
      this._vaultData.set(data as Map<VaultCodeEnum, StoreData[]>);
    } catch (e) {
      this.log.error("VaultExplorerService: Failed to load vault data");
      this.log.error(e);
    } finally {
      this._loading.set(false);
    }
  }

  getItemsByType(type: VaultCodeEnum): StoreData[] {
    return this._vaultData()?.get(type) ?? [];
  }

  /**
   * Gets items from the reader cache directly (all types loaded at startup).
   * Use when you need data for a type that may not be in the active `_vaultData` signal.
   */
  getCachedItemsByType(type: VaultCodeEnum): StoreData[] {
    return this.vaultReader.getDataForType(type) as StoreData[];
  }

  getGroupedItems(type: VaultCodeEnum, includeIdForDedup = false): VaultItemGroup[] {
    return this.groupItemsWithStandardDedup(this.getItemsByType(type), includeIdForDedup, type);
  }

  /** Groups arbitrary items using the same hash rules as {@link getGroupedItems}. */
  groupItemsWithStandardDedup(
    items: StoreData[],
    includeIdForDedup: boolean,
    type: VaultCodeEnum,
  ): VaultItemGroup[] {
    const groups = new Map<string, VaultItemGroup>();

    for (const item of items) {
      const hash = this.contentHash(item, includeIdForDedup, type);
      if (groups.has(hash)) {
        const group = groups.get(hash);
        group.all.push(item);
        group.count = group.all.length;
      } else {
        groups.set(hash, {
          contentHash: hash,
          representative: item,
          all: [item],
          count: 1,
        });
      }
    }

    return this.applyExplorerDisplayOrder(Array.from(groups.values()));
  }

  /**
   * Groups transactions (or any store rows) by normalized `id` only. Same id ⇒ one group;
   * rows with missing/empty id share a single sentinel bucket.
   */
  groupItemsByTransactionId(items: StoreData[]): VaultItemGroup[] {
    const groups = new Map<string, VaultItemGroup>();
    const emptyKey = "__no_transaction_id__";

    for (const item of items) {
      const o = item as Record<string, unknown>;
      const raw = String(o["id"] ?? "").trim() || String(o["_id"] ?? "").trim();
      const hash = raw !== "" ? raw : emptyKey;

      if (groups.has(hash)) {
        const group = groups.get(hash);
        group.all.push(item);
        group.count = group.all.length;
      } else {
        groups.set(hash, {
          contentHash: hash,
          representative: item,
          all: [item],
          count: 1,
        });
      }
    }

    return this.applyExplorerDisplayOrder(Array.from(groups.values()));
  }

  /**
   * Groups source-transaction vault rows by trimmed `srId` (same value ⇒ same linked transaction).
   * Rows with missing/empty `srId` share one sentinel bucket.
   */
  groupItemsBySourceTransactionSrId(items: StoreData[]): VaultItemGroup[] {
    const groups = new Map<string, VaultItemGroup>();
    const emptyKey = "__no_source_transaction_sr_id__";

    for (const item of items) {
      const o = item as Record<string, unknown>;
      const raw = String(o["srId"] ?? "").trim();
      const hash = raw !== "" ? raw : emptyKey;

      if (groups.has(hash)) {
        const group = groups.get(hash);
        group.all.push(item);
        group.count = group.all.length;
      } else {
        groups.set(hash, {
          contentHash: hash,
          representative: item,
          all: [item],
          count: 1,
        });
      }
    }

    return this.applyExplorerDisplayOrder(Array.from(groups.values()));
  }

  /**
   * Vault category-like types: duplicates share the same display name (trimmed).
   * - {@link VaultCodeEnum.Category}: field `n`, fallback `name`
   * - {@link VaultCodeEnum.SourceCategory}: field `nm`, fallback `name`
   */
  groupItemsByCategoryDisplayName(items: StoreData[], type: VaultCodeEnum): VaultItemGroup[] {
    const groups = new Map<string, VaultItemGroup>();
    const emptyKey = "__no_category_name__";

    const pickName = (item: StoreData): string => {
      const o = item as Record<string, unknown>;
      if (type === VaultCodeEnum.SourceCategory) {
        return String(o["nm"] ?? o["name"] ?? "").trim();
      }
      return String(o["n"] ?? o["name"] ?? "").trim();
    };

    for (const item of items) {
      const raw = pickName(item);
      const hash = raw !== "" ? raw : emptyKey;

      if (groups.has(hash)) {
        const group = groups.get(hash);
        group.all.push(item);
        group.count = group.all.length;
      } else {
        groups.set(hash, {
          contentHash: hash,
          representative: item,
          all: [item],
          count: 1,
        });
      }
    }

    return this.applyExplorerDisplayOrder(Array.from(groups.values()));
  }

  async deleteDuplicates(type: VaultCodeEnum, group: VaultItemGroup): Promise<number> {
    if (group.count <= 1) {
      return 0;
    }

    const sorted = [...group.all].sort(
      (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
    );
    const duplicates = sorted.slice(1);

    const modelName = this.getModelName(type);
    const modelsToDelete = duplicates.map((d) => ({ ...d, del: true }));

    try {
      await this.daliService.save(modelName, modelsToDelete);
      this.log.info(
        `VaultExplorerService: Deleted ${duplicates.length} duplicates of type ${type}`,
      );
    } catch (e) {
      this.log.error("VaultExplorerService: Failed to delete duplicates");
      this.log.error(e);
      throw e;
    }

    return duplicates.length;
  }

  /**
   * For every duplicate group (same content hash), deletes all but the oldest copy (by `dc`).
   * Uses the same grouping as {@link getGroupedItems} for the given `includeIdForDedup`.
   */
  async deleteAllDuplicatesForType(
    type: VaultCodeEnum,
    includeIdForDedup: boolean,
  ): Promise<number> {
    const groups = this.getGroupedItems(type, includeIdForDedup);
    const allToDelete: StoreData[] = [];
    for (const group of groups) {
      if (group.count <= 1) {
        continue;
      }
      const sorted = [...group.all].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );
      allToDelete.push(...sorted.slice(1));
    }
    if (allToDelete.length === 0) {
      return 0;
    }
    const modelName = this.getModelName(type);
    await this.deleteVaultItems(modelName, allToDelete);
    return allToDelete.length;
  }

  /**
   * Categories / source categories: duplicate iff display name matches; keeps newest by `dc` per name.
   */
  async deleteAllDuplicatesForVaultCategoryDisplayName(type: VaultCodeEnum): Promise<number> {
    if (type !== VaultCodeEnum.Category && type !== VaultCodeEnum.SourceCategory) {
      return 0;
    }
    const groups = this.groupItemsByCategoryDisplayName(this.getItemsByType(type), type);
    const allToDelete: StoreData[] = [];
    for (const group of groups) {
      if (group.count <= 1) {
        continue;
      }
      const sorted = [...group.all].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );
      allToDelete.push(...sorted.slice(1));
    }
    if (allToDelete.length === 0) {
      return 0;
    }
    const modelName = this.getModelName(type);
    await this.deleteVaultItems(modelName, allToDelete);
    return allToDelete.length;
  }

  async deleteVaultItems(modelName: StoreModelNames, items: StoreData[]): Promise<void> {
    if (items.length === 0) {
      return;
    }
    const modelsToDelete = items.map((i) => ({ ...i, del: true }));
    try {
      await this.daliService.save(modelName, modelsToDelete);
      this.log.info(`VaultExplorerService: Deleted ${items.length} item(s) of type ${modelName}`);
    } catch (e) {
      this.log.error("VaultExplorerService: Failed to delete vault items");
      this.log.error(e);
      throw e;
    }
  }

  /**
   * Groups items using a custom set of excluded keys for the content hash.
   * Callers control exactly which fields are ignored during dedup.
   */
  getGroupedItemsCustom(type: VaultCodeEnum, excludeKeys: string[]): VaultItemGroup[] {
    return this.groupItemsByExcludeKeys(this.getItemsByType(type), excludeKeys);
  }

  /**
   * Same as {@link getGroupedItemsCustom} but reads from the vault reader cache (decrypted vault),
   * without requiring {@link loadVaultDataForType}.
   */
  getGroupedItemsCustomFromVaultReader(
    type: VaultCodeEnum,
    excludeKeys: string[],
  ): VaultItemGroup[] {
    return this.groupItemsByExcludeKeys(this.getCachedItemsByType(type), excludeKeys);
  }

  /** Groups arbitrary items using excluded-key hashing (same as custom dedup in explorer). */
  groupItemsByExcludeKeys(items: StoreData[], excludeKeys: string[]): VaultItemGroup[] {
    const groups = new Map<string, VaultItemGroup>();
    const excl = new Set(excludeKeys);

    for (const item of items) {
      const hash = this.contentHashCustom(item, excl);
      if (groups.has(hash)) {
        const group = groups.get(hash);
        group.all.push(item);
        group.count = group.all.length;
      } else {
        groups.set(hash, {
          contentHash: hash,
          representative: item,
          all: [item],
          count: 1,
        });
      }
    }

    return this.applyExplorerDisplayOrder(Array.from(groups.values()));
  }

  /**
   * Source explorer: newest `dc` first within each group; groups ordered by the latest `dc`
   * among their members (duplicate groups rank by newest duplicate).
   */
  private applyExplorerDisplayOrder(groups: VaultItemGroup[]): VaultItemGroup[] {
    const normalized = groups.map((g) => {
      const allSorted = [...g.all].sort(
        (a, b) => this.itemDateCreatedMs(b) - this.itemDateCreatedMs(a),
      );
      const representative = allSorted[0] ?? g.representative;
      return {
        ...g,
        representative,
        all: allSorted,
        count: allSorted.length,
      };
    });
    normalized.sort((a, b) => this.groupMaxDateCreatedMs(b) - this.groupMaxDateCreatedMs(a));
    return normalized;
  }

  private itemDateCreatedMs(item: StoreData): number {
    const raw = (item as Record<string, unknown>)["dc"];
    if (raw == null || raw === "") {
      return 0;
    }
    const t = new Date(raw as string | number | Date).getTime();
    return Number.isFinite(t) ? t : 0;
  }

  private groupMaxDateCreatedMs(group: VaultItemGroup): number {
    if (group.all.length === 0) {
      return 0;
    }
    return Math.max(...group.all.map((i) => this.itemDateCreatedMs(i)));
  }

  typeLabel(code: VaultCodeEnum): string {
    return StoreModelNameByVaultCode[code]?.replace("StoreModel", "") ?? code;
  }

  private getModelName(code: VaultCodeEnum): StoreModelNames {
    return StoreModelNameByVaultCode[code] ?? "StoreModel";
  }

  private contentHash(item: StoreData, includeIdForDedup: boolean, type?: VaultCodeEnum): string {
    const o = item as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => {
      if (k === "vid" || k === "dc" || k === "dm" || k === "del") {
        return false;
      }
      if (!includeIdForDedup && (k === "id" || k === "_id")) {
        return false;
      }
      if (type === VaultCodeEnum.Transaction && k === "srId") {
        return false;
      }
      return true;
    });
    keys.sort();
    const sorted: Record<string, unknown> = {};
    for (const k of keys) {
      sorted[k] = o[k];
    }
    return JSON.stringify(sorted);
  }

  private contentHashCustom(item: StoreData, excludeKeys: Set<string>): string {
    const o = item as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => !excludeKeys.has(k));
    keys.sort();
    const sorted: Record<string, unknown> = {};
    for (const k of keys) {
      sorted[k] = o[k];
    }
    return JSON.stringify(sorted);
  }
}
